import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  LinearProgress,
  Chip,
  Button,
  Divider
} from "@mui/material";
import { VariableSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import * as XLSX from "xlsx";
//import ReportService from "../../services/ReportService";
import { useReportDataService } from "../../services/ReportDataService";
import { useNavigate, useLocation } from "react-router-dom";


const columnDefs = [
  { key: "Group_Employee_Code", label: "Employee Code", flex: 1.5 },
  { key: "Full_Name", label: "Full Name", flex: 2 },
  { key: "Mobile", label: "Mobile", flex: 1.5 },
  { key: "Business_Vertical_Desc", label: "Vertical", flex: 1.5},
  { key: "Business_Sub_Vertical_Description", label: "Sub-Vertical", flex: 2.5 },
  { key: "FaceDataStatus", label: "Face Data", flex: 1.5 },
  { key: "FaceValidationRequired", label: "Face Validation", flex: 1.5 },
  { key: "LocationValidationRequired", label: "Location Validation", flex: 1.5 },
  { key: "LocationAssigned", label: "Location Assigned", flex: 1.5 },
  { key: "UsingNewApp", label: "New App", flex: 1.2 },
  { key: "CheckedInAtleastOnce", label: "Checked In", flex: 1.2 }
];

const ROW_HEIGHT = 36;

const ValidationRequirementsReport = ({empID = "All"}) => {
    const { fetchEmployeeValidationData } = useReportDataService();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState({});
  const [loading, setLoading] = useState(true);
  const listRef = useRef();

  const navigate = useNavigate();

 useEffect(() => {
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetchEmployeeValidationData({ employeeID: empID || "All" },false);
      setData(response || []);
    } catch (err) {
      console.error("Error fetching employee validation data", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [empID]); 


  const filteredData = useMemo(() => {
    return data.filter((item) =>
      columnDefs.every(({ key }) => {
        const term = search[key]?.toLowerCase() || "";
        return item[key]?.toString().toLowerCase().includes(term);
      })
    );
  }, [data, search]);

  const Row = ({ index, style }) => {
    const row = filteredData[index];
    return (
      <Box
        style={style}
        sx={{
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #eee",
          px: 0.8,
          fontSize: "0.875rem",
          lineHeight: 1.4,
          minHeight: ROW_HEIGHT
        }}
      >
        {columnDefs.map(({ key, flex }) => (
          <Box
            key={key}
            sx={{
              flex,
              minWidth: 0,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              pr: 1
            }}
          >
            {["Yes", "No"].includes(row[key]) ? (
              <Chip
                label={row[key]}
                size="small"
                sx={{ fontSize: "0.65rem", height: 22 }}
                color={row[key] === "Yes" ? "success" : "error"}
              />
            ) : (
              row[key] || "-"
            )}
          </Box>
        ))}

       
      </Box>
    );
  };

  const handleExport = () => {
     const ws = XLSX.utils.json_to_sheet(filteredData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "EmployeeValidationReport");
    XLSX.writeFile(wb, "EmployeeValidationReport.xlsx");
  };

   const handleLogClick = () => {    
    navigate("/ValidationRequirementsLog");
  };

  return (
    <Box sx={{ p: 1 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        <Typography variant="h2">Validation Requirements</Typography>
        <Box display="flex" gap={1}>
          <Button variant="contained" onClick={handleLogClick}>
              View Logs
            </Button>
            <Button variant="contained" onClick={handleExport}>
              Export to Excel
            </Button>
        </Box>
      
      </Box>

      <Divider sx={{ my: 1 }} />

      {loading ? (
        <LinearProgress />
      ) : (
        <Box component={Paper}>
          {/* Header Row */}
          <Box sx={{ display: "flex", p: 1, borderBottom: "1px solid #ccc" }}>
            {columnDefs.map(({ key, label, flex }) => (
              <Box key={key} sx={{ flex, pr: 1, minWidth: 0 }}>
                <TextField
                  label={label}
                  variant="standard"
                  value={search[key] || ""}
                  onChange={(e) =>
                    setSearch((prev) => ({ ...prev, [key]: e.target.value }))
                  }
                  fullWidth
                  size="small"
                />
              </Box>
            ))}
         
          </Box>

          <Box sx={{ height: "70vh" }}>
            <AutoSizer>
              {({ height, width }) => (
                <List
                  height={height}
                  width={width}
                  itemCount={filteredData.length}
                  itemSize={() => ROW_HEIGHT}
                  itemKey={(index) =>
                    filteredData[index].Group_Employee_Code || index
                  }
                  ref={listRef}
                >
                  {Row}
                </List>
              )}
            </AutoSizer>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ValidationRequirementsReport;
